/**
 * Where a sysadmin should land when an emulation session is activated with a
 * captured landing URL (the "Reproduce as reporter" flow: the support widget
 * records the absolute `page_url` the reporter was on, and the sysadmin wants
 * to open exactly that page as them).
 *
 * The emulation session only exists on the origin that minted it — the
 * emulation JWT is `type: "emulation"` and cannot be transferred through
 * `/cross-domain/create-code`, which by design only mints transfer codes for a
 * plain `type: "access"` token whose `sub` is the transferred profile. So a
 * captured URL on another PanLuma host is followed by PATH on the current
 * origin: the SPA serves every route on every app host and reads tenant scope
 * from the token, not the hostname.
 *
 * A URL outside our zone (the widget embedded on a customer's own site) or on
 * a non-http scheme has no corresponding app route, so it resolves to the
 * dashboard.
 */

import { isPanlumaAppHost } from './panlumaZone';

const DASHBOARD = '/';

/**
 * Resolve the same-origin path to navigate to after activating emulation.
 *
 * @param landingUrl Absolute or relative URL captured from the reporter.
 * @param origin The current `window.location.origin`.
 */
export function resolveEmulationLandingPath(
  landingUrl: string | undefined,
  origin: string,
): string {
  if (!landingUrl) return DASHBOARD;

  let url: URL;
  try {
    url = new URL(landingUrl, origin);
  } catch {
    return DASHBOARD;
  }

  // `panclaude://cockpit` and friends parse fine but address no web page.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return DASHBOARD;

  const path = `${url.pathname}${url.search}${url.hash}`;

  // `https://app.panluma.ai//evil.com/x` parses as OUR origin with pathname
  // `//evil.com/x`. Assigned to window.location.href that is a protocol-
  // relative reference and the browser leaves for evil.com — so a same-origin
  // check alone does not make the path safe to navigate to. `page_url` is
  // captured by the unauthenticated widget endpoint, i.e. fully reporter-
  // controlled. No app route begins with `//`.
  if (path.startsWith('//')) return DASHBOARD;

  if (url.origin === origin) return path;

  // A different port on the same host is the same site (local dev runs the app
  // on per-worktree ports), as is any other app host in our zone.
  const sameHost = url.hostname === new URL(origin).hostname;
  return sameHost || isPanlumaAppHost(url.hostname) ? path : DASHBOARD;
}
